import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Compass,
  Flame,
  Image,
  LifeBuoy,
  Lock,
  Mic,
  ShieldCheck,
  Sparkles,
  User,
  Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { helpSectionHref } from '../../content/helpGuidesContent';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';

const HELP_SECTION_ICONS = {
  'getting-started': Compass,
  'voice-recording': Mic,
  'story-studio': Sparkles,
  'person-profiles': User,
  'memorial-bridge': Flame,
  keepsakes: Image,
  'handover-vault': Lock,
  'legacy-book': BookOpen,
  'family-gathering-room': Users,
  'account-privacy': ShieldCheck,
  troubleshooting: LifeBuoy,
};

/**
 * Subtle contextual link to a Help & Guides section.
 */
export function HelpContextLink({ section, children, className }) {
  return (
    <Link
      to={helpSectionHref(section)}
      className={cn(
        'text-xs text-[#7a7268] underline-offset-2 transition hover:text-[#5c534a] hover:underline',
        className,
      )}
      data-testid={section ? `help-link-${section}` : 'help-link'}
    >
      {children}
    </Link>
  );
}

export function HelpStepList({ items, className }) {
  if (!items?.length) return null;
  return (
    <ol className={cn('list-decimal space-y-2.5 pl-5 text-sm leading-relaxed text-[#5c534a]', className)}>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ol>
  );
}

export function HelpTroubleshootingCard({ title = 'If something goes wrong', items, className }) {
  if (!items?.length) return null;
  return (
    <div
      className={cn(
        'rounded-xl border border-[#e8e2d8]/90 bg-[#fffdf8] px-4 py-4 sm:px-5',
        className,
      )}
      data-testid="help-troubleshooting-card"
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8b7355]">{title}</p>
      <ul className="mt-3 space-y-2 text-sm leading-relaxed text-[#5c534a]">
        {items.map((item) => (
          <li key={item} className="flex gap-2">
            <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[#c9a961]/80" aria-hidden />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function HelpCallout({ children, className }) {
  if (!children) return null;
  return (
    <p
      className={cn(
        'rounded-xl border border-[#c9a961]/25 bg-[#fffaf3]/80 px-4 py-3 text-sm leading-relaxed text-[#5c4a32]',
        className,
      )}
    >
      {children}
    </p>
  );
}

export function HelpGuideSectionContent({ what, howTo, meetingIdeas, troubleshooting, why, callout, children, className }) {
  return (
    <div className={cn('space-y-5', className)}>
      {what ? (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#3d5c4a]">What this does</p>
          <p className="mt-2 text-sm leading-relaxed text-[#5c534a]">{what}</p>
        </div>
      ) : null}

      {howTo?.length ? (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#3d5c4a]">How to use it</p>
          <div className="mt-2">
            <HelpStepList items={howTo} />
          </div>
        </div>
      ) : null}

      {meetingIdeas?.length ? (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#3d5c4a]">Suggested meeting ideas</p>
          <ul className="mt-2 space-y-2 text-sm leading-relaxed text-[#5c534a]">
            {meetingIdeas.map((item) => (
              <li key={item} className="flex gap-2">
                <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[#c9a961]/80" aria-hidden />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {troubleshooting?.length ? (
        <HelpTroubleshootingCard items={troubleshooting} />
      ) : null}

      {why ? (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#3d5c4a]">Why this helps</p>
          <p className="mt-2 text-sm italic leading-relaxed text-[#6c6258]">{why}</p>
        </div>
      ) : null}

      {callout ? <HelpCallout>{callout}</HelpCallout> : null}

      {children}
    </div>
  );
}

/** @deprecated Use HelpGuideAccordion — kept for compatibility if imported elsewhere */
export function HelpGuideSection({ id, title, what, howTo, troubleshooting, why, callout, children }) {
  return (
    <section
      id={id}
      className="scroll-mt-24 rounded-2xl border border-[#e8e2d8]/80 bg-white/90 p-5 shadow-sm sm:p-7"
      data-testid={`help-section-${id}`}
      aria-labelledby={`help-heading-${id}`}
    >
      <h2 id={`help-heading-${id}`} className="font-serif text-xl font-light text-[#1c1917] sm:text-2xl">
        {title}
      </h2>
      <HelpGuideSectionContent
        className="mt-4"
        what={what}
        howTo={howTo}
        troubleshooting={troubleshooting}
        why={why}
        callout={callout}
      >
        {children}
      </HelpGuideSectionContent>
    </section>
  );
}

export function HelpGuideAccordion({ sections, openSections, onOpenChange }) {
  return (
    <Accordion
      type="multiple"
      value={openSections}
      onValueChange={onOpenChange}
      className="space-y-3"
      data-testid="help-guide-accordion"
    >
      {sections.map((section) => {
        const Icon = HELP_SECTION_ICONS[section.id] || Compass;
        const isOpen = openSections.includes(section.id);

        return (
          <AccordionItem
            key={section.id}
            value={section.id}
            id={section.id}
            className="scroll-mt-24 overflow-hidden rounded-2xl border border-b-0 border-[#e8e2d8]/80 bg-white/90 shadow-sm data-[state=open]:border-[#c9a961]/20"
            data-testid={`help-section-${section.id}`}
          >
            <AccordionTrigger
              className="gap-3 px-4 py-4 hover:no-underline sm:gap-4 sm:px-6 sm:py-5"
              aria-labelledby={`help-heading-${section.id}`}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#f5efe6]/80 text-[#8b7355]/75 ring-1 ring-[#e8e2d8]/80 sm:h-10 sm:w-10">
                <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden />
              </span>
              <span className="min-w-0 flex-1 text-left">
                <span
                  id={`help-heading-${section.id}`}
                  className="block font-serif text-lg font-light text-[#1c1917] sm:text-xl"
                >
                  {section.title}
                </span>
                {!isOpen && section.summary ? (
                  <span className="mt-1 block text-sm leading-snug text-[#7a7268]">{section.summary}</span>
                ) : null}
              </span>
              <span className="hidden shrink-0 text-xs font-medium text-[#8b7355] sm:inline">
                {isOpen ? 'Close guide' : 'Open guide'}
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-0">
              <HelpGuideSectionContent
                className="border-t border-[#e8e2d8]/60 px-5 pb-6 pt-5 sm:px-7 sm:pb-7 sm:pt-6"
                what={section.what}
                howTo={section.howTo}
                meetingIdeas={section.meetingIdeas}
                troubleshooting={section.troubleshooting}
                why={section.why}
                callout={section.callout}
              />
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

export function HelpQuickHelpRow({ cards, onOpenSection, className }) {
  if (!cards?.length) return null;

  return (
    <div
      className={cn('grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4', className)}
      data-testid="help-quick-help-row"
    >
      {cards.map((card) => (
        <article
          key={card.sectionId}
          className="flex flex-col rounded-2xl border border-[#e8e2d8]/80 bg-[#fffdf8]/95 p-4 shadow-sm sm:p-5"
          data-testid={`help-quick-card-${card.sectionId}`}
        >
          <h2 className="font-serif text-base font-light text-[#1c1917] sm:text-lg">{card.title}</h2>
          <p className="mt-2 flex-1 text-sm leading-relaxed text-[#5c534a]">{card.text}</p>
          <button
            type="button"
            onClick={() => onOpenSection(card.sectionId)}
            className="mt-4 inline-flex items-center text-left text-sm font-medium text-[#3d5c4a] underline decoration-[#c9a961]/40 underline-offset-4 transition hover:text-[#2c2824]"
          >
            {card.linkLabel}
          </button>
        </article>
      ))}
    </div>
  );
}

export function HelpGuideNav({ items, activeId, onItemClick, className }) {
  return (
    <nav
      className={cn('space-y-0.5', className)}
      aria-label="Guide index"
      data-testid="help-guide-nav"
    >
      {items.map((item) => {
        const isActive = activeId === item.id;
        return (
          <a
            key={item.id}
            href={`#${item.id}`}
            onClick={(e) => {
              e.preventDefault();
              onItemClick?.(item.id);
            }}
            className={cn(
              'block rounded-lg px-3 py-2 text-sm transition',
              isActive
                ? 'bg-[#f5efe6] font-medium text-[#2c2824]'
                : 'text-[#5c534a] hover:bg-[#fffaf3] hover:text-[#2c2824]',
            )}
            aria-current={isActive ? 'true' : undefined}
          >
            {item.label}
          </a>
        );
      })}
    </nav>
  );
}

export { HelpContextLink as default };
